<template>
  <div class="page service">
    <page-banner
      title="服务中心"
      eyebrow="CUSTOMER SERVICE"
      subtitle="官方购票入口、企业微信客服、联系电话与投诉建议，为您提供全方位出行服务"
    />
    <div class="page-body" v-loading="loading">
      <el-row :gutter="28">
        <el-col :xs="24" :md="8">
          <el-card id="ticket" class="service-card" shadow="hover">
            <div class="service-card__icon service-card__icon--wechat">
              <i class="el-icon-mobile-phone" />
            </div>
            <h3>官方购票入口</h3>
            <p>关注「百年票票」官方微信公众号，获取演出信息、特惠票价与在线购票服务。</p>
            <div class="service-card__qr">
              <img
                v-if="config.wechatOfficialQrImage"
                :src="resolveImage(config.wechatOfficialQrImage)"
                alt="百年票票官方公众号"
              />
              <div v-else class="service-card__qr-placeholder">
                <i class="el-icon-picture-outline" />
                <span>公众号展示图</span>
              </div>
            </div>
            <p class="service-card__tip">长按识别二维码 · 关注公众号购票</p>
          </el-card>
        </el-col>

        <el-col :xs="24" :md="8">
          <el-card id="wework" class="service-card" shadow="hover">
            <div class="service-card__icon service-card__icon--wework">
              <i class="el-icon-chat-dot-round" />
            </div>
            <h3>企业微信客服</h3>
            <p>扫码添加企业微信客服，在线咨询购票、行程安排与演出场次等任何问题。</p>
            <div class="service-card__qr">
              <img
                v-if="config.weworkQrImage"
                :src="resolveImage(config.weworkQrImage)"
                alt="企业微信客服"
              />
              <div v-else class="service-card__qr-placeholder">
                <i class="el-icon-picture-outline" />
                <span>企微客服二维码</span>
              </div>
            </div>
            <p class="service-card__tip">工作时间在线响应 · 专业出行顾问</p>
          </el-card>
        </el-col>

        <el-col :xs="24" :md="8">
          <el-card id="phone" class="service-card service-card--phone" shadow="hover">
            <div class="service-card__icon service-card__icon--phone">
              <i class="el-icon-phone-outline" />
            </div>
            <h3>联系电话</h3>
            <p>如需电话沟通，欢迎在工作时间内拨打客服热线，我们将竭诚为您服务。</p>
            <div class="service-card__phone-wrap">
              <a :href="'tel:' + phoneDigits" class="service-card__phone">{{ config.contactPhone }}</a>
              <p class="service-card__hours">
                <i class="el-icon-time" />
                {{ config.serviceHours }}
              </p>
            </div>
            <ul class="service-card__features">
              <li><i class="el-icon-circle-check" /> 演出票务咨询</li>
              <li><i class="el-icon-circle-check" /> 一日游行程定制</li>
              <li><i class="el-icon-circle-check" /> 团体预订服务</li>
            </ul>
          </el-card>
        </el-col>
      </el-row>

      <section id="feedback" class="section-block">
        <div class="section-head">
          <h2>投诉建议</h2>
          <p>如对购票、行程安排或客服服务有任何意见，欢迎在此留言，我们将尽快处理并回复</p>
        </div>

        <el-card class="feedback-card" shadow="never">
          <el-form
            ref="feedbackForm"
            :model="feedbackForm"
            :rules="feedbackRules"
            label-width="88px"
            class="feedback-form"
          >
            <el-row :gutter="24">
              <el-col :xs="24" :sm="12">
                <el-form-item label="您的姓名" prop="name">
                  <el-input v-model="feedbackForm.name" placeholder="选填，便于我们称呼您" maxlength="50" />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12">
                <el-form-item label="联系方式" prop="contact">
                  <el-input v-model="feedbackForm.contact" placeholder="请输入手机号或邮箱" maxlength="100" />
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item label="反馈内容" prop="content">
              <el-input
                v-model="feedbackForm.content"
                type="textarea"
                :rows="6"
                placeholder="请详细描述您的问题或建议，例如：购票流程、演出场次、客服响应等"
                maxlength="2000"
                show-word-limit
              />
            </el-form-item>
            <el-form-item>
              <el-button type="danger" :loading="submitting" @click="handleSubmitFeedback">
                提交反馈
              </el-button>
              <el-button @click="resetFeedbackForm">重置</el-button>
            </el-form-item>
          </el-form>
          <div class="feedback-tips">
            <p><i class="el-icon-info" /> 提交后请耐心等待，工作人员将在 1–3 个工作日内处理。</p>
            <p><i class="el-icon-phone-outline" /> 紧急问题可直接拨打客服热线：<a :href="'tel:' + phoneDigits">{{ config.contactPhone }}</a></p>
          </div>
        </el-card>
      </section>
    </div>
  </div>
</template>

<script>
import PageBanner from '@/components/PageBanner.vue';
import { getAppConfig } from '@/api/appConfig';
import { submitFeedback } from '@/api/feedback';
import { resolveImageUrl } from '@/utils/image';
import { DEFAULT_APP_CONFIG } from '@/constants/portal';

export default {
  name: 'ServiceCenter',
  components: { PageBanner },
  data() {
    return {
      loading: false,
      submitting: false,
      config: { ...DEFAULT_APP_CONFIG },
      feedbackForm: {
        name: '',
        contact: '',
        content: ''
      },
      feedbackRules: {
        contact: [
          { required: true, message: '请填写联系方式', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请填写反馈内容', trigger: 'blur' },
          { min: 5, message: '反馈内容至少 5 个字', trigger: 'blur' }
        ]
      }
    };
  },
  computed: {
    phoneDigits() {
      return String(this.config.contactPhone || '').replace(/[^\d+]/g, '');
    }
  },
  created() {
    this.loadConfig();
  },
  mounted() {
    this.scrollToHash();
  },
  watch: {
    '$route.hash'() {
      this.scrollToHash();
    }
  },
  methods: {
    loadConfig() {
      this.loading = true;
      getAppConfig()
        .then(res => {
          if (res.data) {
            this.config = {
              wechatOfficialQrImage: res.data.wechatOfficialQrImage || '',
              weworkQrImage: res.data.weworkQrImage || '',
              contactPhone: res.data.contactPhone || DEFAULT_APP_CONFIG.contactPhone,
              serviceHours: res.data.serviceHours || DEFAULT_APP_CONFIG.serviceHours
            };
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    resolveImage(src) {
      return resolveImageUrl(src);
    },
    scrollToHash() {
      if (!this.$route.hash) return;
      this.$nextTick(() => {
        const el = document.querySelector(this.$route.hash);
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 150);
        }
      });
    },
    handleSubmitFeedback() {
      this.$refs.feedbackForm.validate(valid => {
        if (!valid) return;
        this.submitting = true;
        submitFeedback({ ...this.feedbackForm })
          .then(() => {
            this.$message.success('提交成功，感谢您的反馈！');
            this.resetFeedbackForm();
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
    resetFeedbackForm() {
      this.feedbackForm = { name: '', contact: '', content: '' };
      this.$nextTick(() => {
        if (this.$refs.feedbackForm) {
          this.$refs.feedbackForm.clearValidate();
        }
      });
    }
  }
};
</script>

<style scoped>
.service-card {
  margin-bottom: 28px;
  border-radius: 14px;
  border: none;
  text-align: center;
  min-height: 420px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.service-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 36px rgba(196, 30, 58, 0.1);
}
.service-card__icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  color: #fff;
}
.service-card__icon--wechat {
  background: linear-gradient(135deg, #07c160, #06ad56);
}
.service-card__icon--wework {
  background: linear-gradient(135deg, #2d6cdf, #1a4fb5);
}
.service-card__icon--phone {
  background: linear-gradient(135deg, #c41e3a, #8b1538);
}
.service-card h3 {
  margin: 0 0 10px;
  font-size: 20px;
  color: #303133;
}
.service-card > p {
  margin: 0 0 20px;
  font-size: 14px;
  color: #606266;
  line-height: 1.7;
  min-height: 48px;
}
.service-card__qr {
  margin: 0 auto;
  width: 180px;
  height: 180px;
  border-radius: 12px;
  overflow: hidden;
  background: #f8f9fb;
  border: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: center;
}
.service-card__qr img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.service-card__qr-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #c0c4cc;
}
.service-card__qr-placeholder i {
  font-size: 36px;
  margin-bottom: 8px;
}
.service-card__qr-placeholder span {
  font-size: 13px;
  color: #909399;
}
.service-card__qr-placeholder p {
  margin: 8px 0 0;
  font-size: 11px;
  line-height: 1.5;
  color: #c0c4cc;
}
.service-card__tip {
  margin: 16px 0 0 !important;
  font-size: 12px !important;
  color: #c41e3a !important;
  min-height: auto !important;
}
.service-card--phone {
  display: flex;
  flex-direction: column;
}
.service-card__phone-wrap {
  padding: 24px 0;
}
.service-card__phone {
  display: block;
  font-size: 28px;
  font-weight: 700;
  color: #c41e3a;
  text-decoration: none;
  letter-spacing: 2px;
  transition: opacity 0.2s;
}
.service-card__phone:hover {
  opacity: 0.8;
}
.service-card__hours {
  margin: 12px 0 0;
  font-size: 14px;
  color: #909399;
}
.service-card__hours i {
  margin-right: 4px;
  color: #c41e3a;
}
.service-card__features {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  text-align: left;
}
.service-card__features li {
  padding: 8px 0;
  font-size: 14px;
  color: #606266;
  border-bottom: 1px dashed #ebeef5;
}
.service-card__features li:last-child {
  border-bottom: none;
}
.service-card__features i {
  color: #c41e3a;
  margin-right: 8px;
}
@media (max-width: 768px) {
  .service-card {
    min-height: auto;
    margin-bottom: 16px;
  }
  .service-card__phone {
    font-size: 22px;
  }
  .service-card__qr {
    width: 160px;
    height: 160px;
  }
}

.feedback-card {
  border-radius: 14px;
  border: 1px solid #ebeef5;
}

.feedback-form {
  max-width: 760px;
}

.feedback-tips {
  margin-top: 8px;
  padding: 16px 20px;
  background: #fef0f2;
  border-radius: 8px;
}

.feedback-tips p {
  margin: 0 0 8px;
  font-size: 13px;
  color: #606266;
  line-height: 1.7;
}

.feedback-tips p:last-child {
  margin-bottom: 0;
}

.feedback-tips i {
  color: #c41e3a;
  margin-right: 6px;
}

.feedback-tips a {
  color: #c41e3a;
  text-decoration: none;
  font-weight: 600;
}

.feedback-tips a:hover {
  text-decoration: underline;
}
</style>
